import React, { useState, useEffect } from 'react';
import { FileText, User, UserCheck, Calendar, Clock, Eye, Lock, Check, X, ClipboardList, Download } from 'lucide-react';
import useApi from '../../../utils/api-manager/Helper/useApi';
import DocumentPreviewModal from '../../common/ReusableComponent/DocumentPreviewModal';
import { useToastHandler } from '../../common/ToastHandler';
import { ToastContainer } from 'react-toastify';

const SignedDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [, setSelectedDocument] = useState(null);
  const [, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { GetPaginatedData, getHost, getAPI, Post, Get, DownloadFile } = useApi();
  const { handleToast } = useToastHandler();
  const [appliedSignatures, setAppliedSignatures] = useState([]);


  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async ({ page = 1, searchTerm = "", sort = "" } = {}) => {
    try {
      setLoading(true);
      const response = await GetPaginatedData('document-assignment', {
        page,
        search: searchTerm?.trim(),
        queryParams: {
          ...(sort && { sort }),
        },
      });
      const results = response?.data || [];
      const totalPages = Math.ceil((response?.totalCount || 0) / 10);
      setDocuments(results);
      setTotalPages(totalPages);
      setCurrentPage(page);
      return { data: results, totalPages };
    } catch (error) {
      console.error('Error fetching documents:', error);
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setIsModalOpen(true);
  };

  // const closeModal = () => {
  //   setIsModalOpen(false);
  //   setSelectedDocument(null);
  // };

  const openDocumentPreview = async (e, docFile, docId) => {
    e.stopPropagation();
    let fullUrl = docFile.startsWith('http') ? docFile : `${getHost()}/${docFile}`;
    let signatures = [];

    if (docId) {
      try {
        const response = await Get(`${getAPI("document-assignment")}${docId}/apply_signature/`);
        if (response?.signatures?.length > 0) {
          const firstSignature = response.signatures[0];
          // Use signed_document_relpath if available - this means signatures are embedded
          if (firstSignature.signed_document_relpath) {
            fullUrl = `${getHost()}/${firstSignature.signed_document_relpath}`;
            signatures = [];
          } else {
            signatures = response.signatures.map(sig => ({
              id: sig.id,
              x: sig.signature_position_x,
              y: sig.signature_position_y,
              width: sig.signature_width,
              height: sig.signature_height,
              page: sig.signature_page,
              text: sig.signature?.signature_text || sig.signature?.full_name,
              color: sig.signature?.color,
              imageUrl: sig.signature?.signature_image ? `${getHost()}/${sig.signature.signature_image}` : null,
              type: 'applied'
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching signatures:', error);
      }
    }

    setAppliedSignatures(signatures);
    setPreviewUrl(fullUrl);
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewUrl('');
  };

  const handleLockDocument = async (e, docId) => {
    e.stopPropagation();
    try {
      await Post(`${getAPI("document-assignment")}${docId}/lock/`);
      handleToast({ type: "SUCCESS", message: "Document locked successfully" });
      fetchDocuments();
    } catch (error) {
      console.error("Error locking document:", error);
      const errorMessage = error.response?.data?.error || "Failed to lock document";
      handleToast({ type: "ERROR", message: errorMessage });
    }
  };

  const handleDownloadDocument = async (e, docId, docTitle, originalFile) => {
    e.stopPropagation();
    try {
      const response = await Get(`${getAPI("document-assignment")}${docId}/apply_signature/`);
      let downloadUrl = null;
      if (response?.signatures?.length > 0) {
        for (const signature of response.signatures) {
          if (signature.signed_document) {
            downloadUrl = `${getHost()}/${signature.signed_document}`;
            break;
          } else if (signature.signed_document_relpath) {
            downloadUrl = `${getHost()}/${signature.signed_document_relpath}`;
            break;
          }
        }
      }
      if (!downloadUrl) {
        downloadUrl = originalFile.startsWith('http') ? originalFile : `${getHost()}/${originalFile}`;
      }

      if (downloadUrl) {
        const fileResponse = await DownloadFile(downloadUrl);

        const blob = fileResponse.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const urlParts = downloadUrl.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const fileExtension = fileName.split('.').pop().toLowerCase();
        let downloadFileName = docTitle;
        if (fileExtension) {
          downloadFileName = downloadFileName.replace(/\.[^/.]+$/, "");
          downloadFileName += `.${fileExtension}`;
        } else {
          const contentType = fileResponse.headers['content-type'];
          if (contentType?.includes('pdf')) {
            downloadFileName += '.pdf';
          } else if (contentType?.includes('word') || contentType?.includes('document')) {
            downloadFileName += '.docx';
          } else if (contentType?.includes('text')) {
            downloadFileName += '.txt';
          }
        }
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        handleToast({ type: "SUCCESS", message: "Document downloaded successfully" });
      } else {
        handleToast({ type: "ERROR", message: "No document available for download" });
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      handleToast({ type: "ERROR", message: "Failed to download document" });
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      signed: "#28a745",
      sent: "#007bff",
      viewed: "#ffc107",
      draft: "#6c757d",
      completed: "#17a2b8"
    };
    return (
      <span style={{
        padding: "4px 8px",
        fontSize: "12px",
        fontWeight: "600",
        borderRadius: "12px",
        background: colors[status] || "#6c757d",
        color: "#fff",
        textTransform: "uppercase"
      }}>
        {status === 'signed' ? '✓ Signed' : status === 'locked' ? '🔒 Locked' : status}
      </span>
    );
  };



  if (loading) {
    return (
      <div className="responsive-box">

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}><FileText size={48} /></div>
            <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading signed documents...</div>
          </div>
        </div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (

      <div className="responsive-box">

        <div className="inventory-header">
          <div className="inventory-title">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
              <ClipboardList size={28} />
              Signed Documents
            </h2>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.5 }}><FileText size={64} /></div>
            <h3 style={{ color: '#374151', marginBottom: '12px' }}>No Signed Documents</h3>
            <p style={{ color: '#6b7280', fontSize: '16px' }}>There are no signed documents available at the moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box">
      <ToastContainer />
      <div className="inventory-header">
        <div className="inventory-title">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
            <ClipboardList size={28} />
            Signed Documents
          </h2>
        </div>
      </div>

      <div style={{ padding: '20px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '24px'
        }}>
          {documents.map((doc) => (
            <div
              key={doc.id}
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '24px',
                cursor: 'pointer',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={() => handleDocumentClick(doc)}
            >
              {(doc.status === 'completed' || doc.status === 'signed') && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  borderRadius: '0 16px 0 60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'white', fontSize: '20px' }}>✓</span>
                  {/* <Check size={20} color="white" /> */}
                </div>
              )}
              {(doc.status !== 'completed' && doc.status !== 'signed') && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  borderRadius: '0 16px 0 60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'white', fontSize: '20px' }}>✗</span>
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <h3 style={{
                  margin: '0 0 8px 0',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1f2937',
                  lineHeight: '1.4'
                }}>
                  <FileText size={18} style={{ marginRight: '8px' }} /> {doc.document.title}
                </h3>
                <div style={{ marginBottom: '12px' }}>
                  {getStatusBadge(doc.status)}
                </div>
              </div>

              <div style={{ marginBottom: '20px', fontSize: '14px', color: '#6b7280' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <User size={16} />
                  <span><strong>Assigned to:</strong> {doc.assigned_to_first_name} {doc.assigned_to_last_name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <UserCheck size={16} />
                  <span><strong>Assigned by:</strong> {doc.assigned_by_first_name} {doc.assigned_by_last_name}</span>
                </div>
                {doc.signed_at ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={16} />
                    <span>
                      <strong>Signed:</strong> {new Date(doc.signed_at).toLocaleDateString()}
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}>
                    <Clock size={16} />
                    <span><strong>Not signed yet</strong></span>
                  </div>
                )}

              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {/* <button
                  onClick={(e) => openDocumentPreview(e, doc.document.document_file, doc.id)}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Eye size={16} /> View
                </button> */}

                <button
                  onClick={(e) => handleDownloadDocument(e, doc.id, doc.document.title, doc.document.document_file)}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Download size={16} /> Download
                </button>

                {doc.status === 'signed' ? (
                  <button
                    onClick={(e) => handleLockDocument(e, doc.id)}
                    style={{
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Lock size={16} /> Lock
                  </button>
                ) : (
                  <span style={{
                    padding: '8px 16px',
                    background: '#6b7280',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Lock size={16} /> Locked
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          padding: '20px',
          borderTop: '1px solid #e2e8f0'
        }}>
          <button
            onClick={() => fetchDocuments({ page: currentPage - 1 })}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage === 1 ? '#f3f4f6' : '#3b82f6',
              color: currentPage === 1 ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Previous
          </button>

          <span style={{
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151'
          }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => fetchDocuments({ page: currentPage + 1 })}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#3b82f6',
              color: currentPage === totalPages ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Next
          </button>
        </div>
      )}


      <DocumentPreviewModal
        isOpen={isPreviewOpen}
        onClose={closePreview}
        fileUrl={previewUrl}
        signatures={appliedSignatures}
      />
    </div>
  );
};

export default SignedDocuments;